/**
 * Camera RAW / DNG decoding with LibRaw (WebAssembly, runs in its own worker).
 * Output is 16-bit, demosaiced, without auto-brightening, converted to sRGB
 * primaries by LibRaw's camera matrix. The libraw-wasm build ignores the
 * `gamm` setting and always applies LibRaw's default BT.709 curve (0.45 power,
 * 4.5 toe slope, scaled by 0x10000); the frame is therefore tagged with the
 * exact inverse ('bt709') so the pipeline recovers linear sensor values.
 */
import type { RawFrame } from '../../core/types';
import { makeFrame } from '../frame';

export type RawWhiteBalance = 'camera' | 'auto' | 'unity';

export interface RawOptions {
  whiteBalance: RawWhiteBalance;
  /** LibRaw demosaic quality: 0 linear, 1 VNG, 2 PPG, 3 AHD, 4 DCB, 11 DHT, 12 AAHD. */
  quality: number;
  halfSize: boolean;
}

export const DEFAULT_RAW: RawOptions = { whiteBalance: 'camera', quality: 3, halfSize: false };

export async function decodeRaw(bytes: Uint8Array, name: string, opt: RawOptions = DEFAULT_RAW): Promise<RawFrame> {
  const { default: LibRaw } = await import('libraw-wasm');
  const raw = new LibRaw();
  try {
    await raw.open(bytes.slice(), {
      outputBps: 16,
      noAutoBright: true,
      outputColor: 1,
      useCameraWb: opt.whiteBalance === 'camera',
      useAutoWb: opt.whiteBalance === 'auto',
      userMul: opt.whiteBalance === 'unity' ? [1, 1, 1, 1] : null,
      userQual: opt.quality,
      halfSize: opt.halfSize,
      userFlip: -1,
    });
    const meta = await raw.metadata(false);
    const img = await raw.imageData();
    if (!img) throw new Error('LibRaw returned no image data');
    const channels = img.colors;
    const data = img.bits === 16 ? new Uint16Array(img.data.buffer, img.data.byteOffset, img.data.length) : img.data;
    const info: Record<string, string> = { Decoder: 'LibRaw (WebAssembly)' };
    if (meta) {
      if (meta.camera_make || meta.camera_model) info.Camera = `${meta.camera_make} ${meta.camera_model}`.trim();
      if (meta.iso_speed) info.ISO = `${meta.iso_speed}`;
      if (meta.shutter) info.Shutter = meta.shutter >= 1 ? `${meta.shutter} s` : `1/${Math.round(1 / meta.shutter)} s`;
      if (meta.aperture) info.Aperture = `f/${meta.aperture}`;
      if (meta.focal_len) info['Focal length'] = `${meta.focal_len} mm`;
      info['Sensor size'] = `${meta.raw_width} × ${meta.raw_height}`;
    }
    info['White balance'] = opt.whiteBalance;
    info.Demosaic = ['linear', 'VNG', 'PPG', 'AHD', 'DCB'][opt.quality] ?? `${opt.quality}`;
    const frame = makeFrame({
      width: img.width,
      height: img.height,
      data,
      channels,
      maxCode: img.bits === 16 ? 65535 : 255,
      bitDepth: img.bits,
      format: 'raw',
      name,
      encoding: {
        transfer: 'bt709',
        primaries: 'srgb',
        source: 'raw-decoder',
        description: 'Camera RAW via LibRaw: BT.709-encoded 16-bit output, linearised exactly; camera matrix to sRGB primaries',
      },
      info,
      warnings: [
        'RAW values are relative: absolute luminance requires a photometric calibration (exposure, ISO and aperture change the scale).',
      ],
    });
    // LibRaw scales its tone curve by 0x10000, not 0xffff.
    if (img.bits === 16) frame.scale = 1 / 65536;
    return frame;
  } finally {
    raw.dispose();
  }
}
